import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Scale, AlertTriangle, CheckCircle, XCircle, Shield, CreditCard, Phone, Mail, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { companyInfo } from '../data/mock';

const TermsOfServicePage = () => {
  const lastUpdated = 'June 23, 2026';
  const effectiveDate = 'June 23, 2026';

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: CheckCircle,
      content: (
        <>
          <p className="mb-4">
            By accessing or using the services provided by Nanda Diagnostics ("Company," "we," "us," or "our"), including our website (nandadiagnostics.com), mobile application, and diagnostic services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms").
          </p>
          <p className="mb-4">
            If you do not agree to these Terms, you must not access or use our Services. These Terms constitute a legally binding agreement between you and Nanda Diagnostics.
          </p>
          <p className="mb-4">
            We reserve the right to modify these Terms at any time. We will notify you of any changes by updating the "Last Updated" date and, for material changes, by providing additional notice (such as adding a statement to our website or sending you a notification). Your continued use of the Services after any changes constitutes your acceptance of the new Terms.
          </p>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
            <h5 className="font-semibold text-red-800 mb-2">Important Medical Disclaimer</h5>
            <p className="text-red-700 text-sm">
              <strong>NOT A MEDICAL DEVICE:</strong> Nanda Diagnostics provides diagnostic testing services only. Our website and mobile application are informational tools and are NOT medical devices. They do not provide medical advice, diagnosis, or treatment recommendations. Test results must be interpreted by qualified healthcare professionals. Never disregard professional medical advice or delay seeking it because of information obtained from our Services.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'eligibility',
      title: 'Eligibility',
      icon: Shield,
      content: (
        <>
          <p className="mb-4">To use our Services, you must:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Be at least 18 years of age, or have the consent of a parent or legal guardian</li>
            <li>Have the legal capacity to enter into a binding agreement</li>
            <li>Not be prohibited from using our Services under applicable laws</li>
            <li>Provide accurate and complete information when creating an account or booking services</li>
          </ul>
          <p>
            For services provided to minors (persons under 18 years of age), a parent or legal guardian must provide consent and is responsible for the minor&apos;s use of our Services.
          </p>
        </>
      )
    },
    {
      id: 'services',
      title: 'Our Services',
      icon: FileText,
      content: (
        <>
          <p className="mb-4">Nanda Diagnostics provides the following services:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Diagnostic Testing:</strong> Laboratory tests, radiology services, cardiology tests, and other diagnostic procedures</li>
            <li><strong>Health Packages:</strong> Comprehensive health check-up packages</li>
            <li><strong>Online Report Access:</strong> Secure access to your diagnostic reports through our website and mobile application</li>
            <li><strong>Consultation Services:</strong> Access to medical consultants for result interpretation</li>
            <li><strong>Home Sample Collection:</strong> Collection of samples at your preferred location (subject to availability)</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Service Availability</h4>
          <p className="mb-4">
            Our Services are available at our diagnostic centers and through our digital platforms. Service availability may vary by location and is subject to change without notice.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Medical Disclaimer</h4>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800">
              <strong>Important:</strong> Our diagnostic services are intended to assist healthcare providers in making medical decisions. Test results should be interpreted by qualified healthcare professionals. Our Services do not constitute medical advice, diagnosis, or treatment. Always consult with a qualified healthcare provider for medical concerns.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'health-data',
      title: 'Health Data and Medical Records',
      icon: Shield,
      content: (
        <>
          <p className="mb-4">
            Your health data is sensitive information that requires special protection. By using our Services, you acknowledge and agree to the following:
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Collection of Health Data</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>We collect health information necessary to provide diagnostic services</li>
            <li>This includes test results, medical history, prescriptions, and related health records</li>
            <li>Health data is collected with your explicit consent</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Use of Health Data</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>To perform and report diagnostic tests</li>
            <li>To maintain your health records for future reference</li>
            <li>To share with healthcare providers you authorize</li>
            <li>For quality assurance and improvement of services</li>
            <li>For compliance with legal and regulatory requirements</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Data Retention</h4>
          <p className="mb-4">
            Medical records are retained as per applicable healthcare regulations:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Medical records: Minimum 10 years from the date of last visit</li>
            <li>Radiology images: Minimum 3 years (or as per applicable regulations)</li>
            <li>Pathology samples: As per laboratory accreditation requirements</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Data Security</h4>
          <p className="mb-4">
            We implement stringent security measures to protect your health data, including encryption, access controls, and regular security audits. However, no system is completely secure, and you acknowledge this inherent risk.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800">
              <strong>Your Consent:</strong> By using our Services, you consent to the collection, use, and storage of your health data as described in this section and our Privacy Policy.
            </p>
          </div>
        </>
      )
    },
    {
      id: 'account',
      title: 'Account Registration',
      icon: Shield,
      content: (
        <>
          <p className="mb-4">To access certain features of our Services, you may need to create an account. When creating an account, you agree to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain and promptly update your account information</li>
            <li>Maintain the security and confidentiality of your login credentials</li>
            <li>Accept responsibility for all activities under your account</li>
            <li>Notify us immediately of any unauthorized use of your account</li>
          </ul>
          <p className="mb-4">
            We reserve the right to suspend or terminate your account if any information provided is found to be inaccurate, incomplete, or fraudulent.
          </p>
          <p>
            You are responsible for all activities that occur under your account, whether or not you authorized such activities. We are not liable for any loss or damage arising from unauthorized use of your account.
          </p>
        </>
      )
    },
    {
      id: 'booking',
      title: 'Booking and Appointments',
      icon: CheckCircle,
      content: (
        <>
          <h4 className="font-semibold text-gray-800 mb-3">Booking Process</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Appointments can be booked online, via phone, or in person at our centers</li>
            <li>Booking confirmation is subject to availability</li>
            <li>Certain tests may require prior preparation (fasting, etc.); you are responsible for following preparation instructions</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Cancellation and Rescheduling</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Appointments can be cancelled or rescheduled up to 24 hours before the scheduled time</li>
            <li>Late cancellations or no-shows may be subject to a cancellation fee</li>
            <li>We reserve the right to reschedule appointments due to unforeseen circumstances</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Sample Collection</h4>
          <p>
            For tests requiring sample collection, you must follow the preparation instructions provided. Failure to follow instructions may affect test results or require re-collection.
          </p>
        </>
      )
    },
    {
      id: 'payment',
      title: 'Payment Terms',
      icon: CreditCard,
      content: (
        <>
          <h4 className="font-semibold text-gray-800 mb-3">Pricing</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>All prices are listed in the local currency and are inclusive of applicable taxes unless stated otherwise</li>
            <li>Prices are subject to change without prior notice</li>
            <li>The price applicable at the time of booking will be honored for that transaction</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Payment Methods</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>We accept cash, credit cards, debit cards, UPI, and net banking</li>
            <li>Payment is due at the time of service unless otherwise agreed</li>
            <li>For certain packages, advance payment may be required</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Refunds</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Refunds for cancelled tests will be processed within 7-10 business days</li>
            <li>Tests that have already been performed are non-refundable</li>
            <li>Refunds will be credited to the original payment method</li>
            <li>Administrative fees may be deducted from refunds for late cancellations</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Insurance</h4>
          <p>
            We accept claims from select insurance providers. Please verify coverage with your insurance provider before booking. We are not responsible for claims denied by your insurance company.
          </p>
        </>
      )
    },
    {
      id: 'reports',
      title: 'Test Reports and Results',
      icon: FileText,
      content: (
        <>
          <h4 className="font-semibold text-gray-800 mb-3">Report Delivery</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Reports are typically available within the turnaround time specified for each test</li>
            <li>Reports can be accessed online through our website or mobile application</li>
            <li>Physical copies can be collected from our centers or sent via courier (additional charges may apply)</li>
            <li>Turnaround times are estimates and may vary based on test complexity and volume</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Report Accuracy</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>We maintain high standards of quality and accuracy in our testing processes</li>
            <li>All tests are performed by qualified professionals using calibrated equipment</li>
            <li>In case of any discrepancy, you may request a re-test within 7 days of the original report</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Report Confidentiality</h4>
          <p>
            Your test reports are confidential and will only be shared with you or persons you have authorized. Please refer to our Privacy Policy for details on how we protect your health information.
          </p>
        </>
      )
    },
    {
      id: 'prohibited',
      title: 'Prohibited Conduct',
      icon: XCircle,
      content: (
        <>
          <p className="mb-4">When using our Services, you agree NOT to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Provide false, misleading, or fraudulent information</li>
            <li>Impersonate another person or misrepresent your identity</li>
            <li>Use the Services for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to our systems or other users&apos; accounts</li>
            <li>Interfere with or disrupt the integrity or performance of our Services</li>
            <li>Transmit any viruses, malware, or other harmful code</li>
            <li>Harvest or collect information about other users without consent</li>
            <li>Use automated systems (bots, scrapers) to access our Services</li>
            <li>Reproduce, duplicate, or resell any part of our Services without permission</li>
            <li>Use the Services in any way that violates applicable laws or regulations</li>
          </ul>
          <p>
            Violation of these prohibitions may result in immediate termination of your account and access to our Services, and may subject you to legal action.
          </p>
        </>
      )
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      icon: Shield,
      content: (
        <>
          <p className="mb-4">
            All content, features, and functionality of our Services, including but not limited to text, graphics, logos, icons, images, audio clips, software, and the compilation thereof, are the exclusive property of Nanda Diagnostics or its licensors and are protected by copyright, trademark, and other intellectual property laws.
          </p>
          <h4 className="font-semibold text-gray-800 mb-3">Limited License</h4>
          <p className="mb-4">
            We grant you a limited, non-exclusive, non-transferable, revocable license to access and use our Services for your personal, non-commercial use, subject to these Terms.
          </p>
          <h4 className="font-semibold text-gray-800 mb-3">Restrictions</h4>
          <p>You may not:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Copy, modify, or distribute our content without permission</li>
            <li>Use our trademarks or branding without written consent</li>
            <li>Reverse engineer or attempt to extract the source code of our software</li>
            <li>Remove any copyright or proprietary notices from our content</li>
          </ul>
        </>
      )
    },
    {
      id: 'liability',
      title: 'Limitation of Liability',
      icon: AlertTriangle,
      content: (
        <>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800">
              <strong>IMPORTANT:</strong> Please read this section carefully as it limits our liability to you.
            </p>
          </div>

          <p className="mb-4">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
          </p>
          
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND</li>
            <li>WE DO NOT WARRANT THAT OUR SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE</li>
            <li>WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
            <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SPECIFIC SERVICE GIVING RISE TO THE CLAIM</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Medical Limitations</h4>
          <p className="mb-4">
            We are not liable for:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Medical decisions made based on test results without consulting a qualified healthcare provider</li>
            <li>Delays in diagnosis or treatment resulting from reliance solely on our Services</li>
            <li>Actions or inactions of healthcare providers who receive your test results</li>
            <li>Pre-analytical errors caused by improper sample collection or patient preparation</li>
          </ul>
        </>
      )
    },
    {
      id: 'indemnification',
      title: 'Indemnification',
      icon: Shield,
      content: (
        <>
          <p className="mb-4">
            You agree to indemnify, defend, and hold harmless Nanda Diagnostics, its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses (including attorney&apos;s fees) arising from:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Your use of our Services</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights, including privacy rights</li>
            <li>Any content you submit or transmit through our Services</li>
            <li>Your negligent or wrongful conduct</li>
          </ul>
        </>
      )
    },
    {
      id: 'termination',
      title: 'Termination',
      icon: XCircle,
      content: (
        <>
          <h4 className="font-semibold text-gray-800 mb-3">Termination by You</h4>
          <p className="mb-4">
            You may terminate your account at any time by contacting us. Upon termination, you will no longer have access to your account, but we may retain certain information as required by law or for legitimate business purposes.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Termination by Us</h4>
          <p className="mb-4">
            We may terminate or suspend your account and access to our Services immediately, without prior notice or liability, if:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>You breach these Terms</li>
            <li>We are required to do so by law</li>
            <li>We discontinue our Services</li>
            <li>We detect fraudulent or illegal activity</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Effect of Termination</h4>
          <p>
            Upon termination, your right to use our Services will immediately cease. Provisions that by their nature should survive termination (including ownership, warranty disclaimers, indemnification, and limitations of liability) will survive.
          </p>
        </>
      )
    },
    {
      id: 'mobile-application',
      title: 'Mobile Application Terms',
      icon: Phone,
      content: (
        <>
          <p className="mb-4">
            The following additional terms apply to your use of the Nanda Diagnostics mobile application (&quot;App&quot;) available on Google Play Store and Apple App Store.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">App Development</h4>
          <p className="mb-4">
            The Nanda Diagnostics mobile application is developed and maintained by Nanda Diagnostics Pvt. Ltd. The App is designed to provide convenient access to our diagnostic services, test reports, and health information.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">License Grant</h4>
          <p className="mb-4">
            We grant you a limited, non-exclusive, non-transferable, revocable license to download, install, and use the App on your personal mobile device solely for accessing our Services. This license does not allow you to:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Modify, reverse engineer, or decompile the App</li>
            <li>Rent, lease, or transfer the App to third parties</li>
            <li>Use the App for any commercial purpose without authorization</li>
            <li>Remove any copyright or proprietary notices from the App</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">App Features</h4>
          <p className="mb-4">The App includes the following features:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Report Access:</strong> View and download your diagnostic test reports in PDF format</li>
            <li><strong>Appointment Booking:</strong> Schedule tests, health packages, and home sample collection appointments</li>
            <li><strong>Notifications:</strong> Receive push notifications for appointment reminders, test results availability, and promotional offers</li>
            <li><strong>Health Records:</strong> Access your complete test history and health records</li>
            <li><strong>Payment:</strong> Make secure online payments for tests and packages</li>
            <li><strong>Support:</strong> Contact customer support and raise service requests</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">Device Permissions</h4>
          <p className="mb-4">
            The App may request access to certain device features. By granting these permissions, you allow the App to:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Use your camera to capture prescription images and documents</li>
            <li>Access storage to download and save your medical reports</li>
            <li>Send push notifications for important updates</li>
            <li>Access your location to find nearby diagnostic centers (optional)</li>
          </ul>

          <h4 className="font-semibold text-gray-800 mb-3">App Updates</h4>
          <p className="mb-4">
            We may release updates to the App to add new features, fix bugs, or improve performance. You are responsible for keeping the App updated. Continued use of an outdated version may result in limited functionality or security vulnerabilities.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">App Store Terms</h4>
          <p>
            Your use of the App is also subject to the terms and conditions of the app store from which you downloaded it (Google Play Store or Apple App Store). In case of any conflict between these Terms and the app store terms, the app store terms shall prevail for matters relating to app distribution.
          </p>
        </>
      )
    },
    {
      id: 'governing-law',
      title: 'Governing Law and Disputes',
      icon: Scale,
      content: (
        <>
          <h4 className="font-semibold text-gray-800 mb-3">Governing Law</h4>
          <p className="mb-4">
            These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions.
          </p>

          <h4 className="font-semibold text-gray-800 mb-3">Dispute Resolution</h4>
          <p className="mb-4">
            Any dispute arising out of or relating to these Terms or our Services shall be resolved as follows:
          </p>
          <ol className="list-decimal pl-6 mb-4 space-y-2">
            <li><strong>Informal Resolution:</strong> We encourage you to contact us first to seek an informal resolution</li>
            <li><strong>Mediation:</strong> If informal resolution fails, disputes may be submitted to mediation</li>
            <li><strong>Arbitration:</strong> Disputes not resolved through mediation shall be settled by binding arbitration in accordance with the Arbitration and Conciliation Act, 1996</li>
          </ol>

          <h4 className="font-semibold text-gray-800 mb-3">Jurisdiction</h4>
          <p>
            The courts at the location of our registered office shall have exclusive jurisdiction over any disputes arising from these Terms or our Services.
          </p>
        </>
      )
    },
    {
      id: 'contact',
      title: 'Contact Information',
      icon: Mail,
      content: (
        <>
          <p className="mb-4">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#007897] mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-800">Nanda Diagnostics</p>
                <p className="text-gray-600">{companyInfo.address}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#007897] flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-800">Email</p>
                <a href={`mailto:${companyInfo.email}`} className="text-[#007897] hover:underline">
                  {companyInfo.email}
                </a>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-[#007897] flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-800">Phone</p>
                <a href={`tel:${companyInfo.phones[0]}`} className="text-[#007897] hover:underline">
                  {companyInfo.phones[0]}
                </a>
              </div>
            </div>
          </div>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#007897] to-[#005a73]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center text-white">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Scale className="w-8 h-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-6">
              Please read these terms carefully before using our services. By using our services, you agree to be bound by these terms.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-white/80">
              <span>Last Updated: {lastUpdated}</span>
              <span className="hidden sm:inline">|</span>
              <span>Effective Date: {effectiveDate}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-sm text-[#007897] hover:text-[#005a73] hover:underline"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="space-y-12">
            {sections.map((section) => {
              const IconComponent = section.icon;
              return (
                <div
                  key={section.id}
                  id={section.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 scroll-mt-24"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-[#007897]/10 rounded-xl flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-[#007897]" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                  </div>
                  <div className="text-gray-600 leading-relaxed">
                    {section.content}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Related Links */}
          <div className="mt-12 bg-gradient-to-r from-[#007897] to-[#005a73] rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Related Policies</h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Please also review our Privacy Policy to understand how we collect, use, and protect your personal information.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/privacy-policy"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#007897] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
              >
                <Shield className="w-5 h-5" />
                Privacy Policy
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/30 transition-colors"
              >
                <Phone className="w-5 h-5" />
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
